import { useState } from "react";
import { TrendingUp, Calendar, Banknote, Shield, Star, Headphones, ArrowRight } from "lucide-react";
import EnrichmentModal from "@/pages/manchester/EnrichmentModal";
import BookCallModal from "@/components/BookCallModal";

const benefits = [
  { icon: TrendingUp, title: "Higher Income", description: "Earn 40-80% more than a traditional long-term tenancy. Short-let pricing flexes with demand, events, and seasons so your property always earns its full potential." },
  { icon: Calendar, title: "Total Flexibility", description: "Block dates whenever you need your property back. Unlike an AST, you stay in control whether it's a week, a month, or longer." },
  { icon: Banknote, title: "Consistent Cash Flow", description: "Guests pay upfront, every booking. No rent arrears, no chasing payments, no late deposits. Just clean, predictable income hitting your account." },
  { icon: Shield, title: "Better Property Standards", description: "Regular professional cleaning and inspections keep your property in pristine condition, often better maintained than under a long-term tenancy." },
  { icon: Star, title: "No Bad Tenants", description: "Every guest is vetted, ID-verified, and reviewed. No problem tenants, no eviction process, no legal headaches. Just well-screened visitors." },
  { icon: Headphones, title: "Completely Hands-Free", description: "We handle everything from listing and pricing to guest comms, check-in, and housekeeping. You earn without lifting a finger." },
];

export default function WhyShortLet() {
  const [showModal, setShowModal] = useState(false);
  const [showBookCall, setShowBookCall] = useState(false);
  return (
    <section style={{ backgroundColor: "#F4F1E7", padding: "clamp(60px,7vw,96px) clamp(20px,5vw,80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#5C7268", marginBottom: 12, marginTop: 0 }}>
            The smart alternative
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#112215", margin: "0 auto 14px", maxWidth: 640 }}>
            Why short-lets outperform<br />long-term rentals.
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: "#5C7268", lineHeight: 1.6, maxWidth: 500, margin: "0 auto" }}>
            The numbers don't lie. Short-let management consistently outperforms AST tenancies on every metric that matters.
          </p>
        </div>

        <div className="why-shortlet-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {benefits.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="wslet-card"
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: "26px 26px 30px",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 44 }}>
                <span className="why-shortlet-title" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 17.5, color: "#112215", letterSpacing: "-0.01em" }}>
                  {title}
                </span>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#E8E4D5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} color="#5C7268" strokeWidth={1.8} />
                </div>
              </div>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, color: "#5C7268", lineHeight: 1.65, margin: 0 }}>
                {description}
              </p>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 44, flexWrap: "wrap" }}>
          <button
            onClick={() => setShowModal(true)}
            style={{ background: "#CD533B", color: "#fff", border: "none", borderRadius: 8, padding: "14px 28px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#B8462F"}
            onMouseLeave={e => e.currentTarget.style.background = "#CD533B"}
          >
            Get Free Valuation <ArrowRight size={15} />
          </button>
          <button
            onClick={() => setShowBookCall(true)}
            style={{ background: "transparent", color: "#112215", border: "1.5px solid rgba(17,34,21,0.3)", borderRadius: 8, padding: "14px 28px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "border-color 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#112215"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(17,34,21,0.3)"}
          >
            Book a Call
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .why-shortlet-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .why-shortlet-grid { grid-template-columns: 1fr !important; } }
        .wslet-card:hover { background: #F4F1E7 !important; transform: rotate(-1.5deg) scale(1.03); box-shadow: 0 20px 50px rgba(0,0,0,0.12); z-index: 2; position: relative; }
        @media (max-width: 768px) { .why-shortlet-title { font-size: 19px !important; } }
      `}</style>

      {showModal && <EnrichmentModal mode="full" onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}
      {showBookCall && <BookCallModal onClose={() => setShowBookCall(false)} onGetValuation={() => { setShowBookCall(false); setShowModal(true); }} />}
    </section>
  );
}